import React, { useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Tooltip } from "react-leaflet";
import { Spin } from "antd";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import MarkerClusterGroup from "@changey/react-leaflet-markercluster";
import "./VisitDataSummary.scss"
import Preview from "../../planning/VisitBoard/TableView/modals/Preview";
import completedIcon from "../../../../Assets/svg/completedIcon.svg";
import inProgressIcon from "../../../../Assets/svg/inProgressIcon.svg";
import notInitatedIcon from "../../../../Assets/svg/notInitatedIcon.svg";
import postponed from "../../../../Assets/svg/postponedIcon.svg";

const statusConfig = {
  completed: {
    color: "#B6CE84",
    icon: process.env.PUBLIC_URL + completedIcon,
  },
  inprogress: {
    color: "#FFD88C",
    icon: process.env.PUBLIC_URL + inProgressIcon,
  },
  notinitiated: {
    color: "#999",
    icon: process.env.PUBLIC_URL + notInitatedIcon,
  },
  postponed: {
    color: "#E08585",
    icon: process.env.PUBLIC_URL + postponed,
  },
};

const createIcon = (status) => {
  const normalizedStatus = status?.toLowerCase() || "";
  const { color, icon } = statusConfig[normalizedStatus] || {
    color: "#999",
    icon: "/icons/not-initiated.png",
  };

  return L.divIcon({
    className: "custom-icon",
    html: `
      <div style="
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: ${color};
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0px 4px 12px rgba(59, 75, 104, 0.25);
      ">
        <img src='${icon}' alt='${status}' style="width: 20px; height: 20px;" />
      </div>
    `,
    iconSize: [40, 40],
  });
};

const Map = ({ data, loadingData }) => {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedSiteVisit, setSelectedSiteVisit] = useState(null);

  const handleMarkerClick = (siteVisitCardId) => {
    setSelectedSiteVisit(siteVisitCardId);
    setShowPreviewModal(true);
  };

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
    setSelectedSiteVisit(null);
  };

  const adjustedData = useMemo(() => {
    const grouped = {};
    const adjustedMarkers = [];

    data.forEach((marker) => {
      const key = `${marker.latitude},${marker.longitude}`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(marker);
    });

    Object.values(grouped).forEach((group) => {
      if (group.length === 1) {
        adjustedMarkers.push(group[0]);
      } else {
        const angleStep = (2 * Math.PI) / group.length;
        group.forEach((marker, index) => {
          const angle = index * angleStep;
          const offsetLat = 0.0001 * Math.cos(angle);
          const offsetLng = 0.0001 * Math.sin(angle);
          adjustedMarkers.push({
            ...marker,
            latitude: marker.latitude + offsetLat,
            longitude: marker.longitude + offsetLng,
          });
        });
      }
    });

    return adjustedMarkers;
  }, [data]);

  return (
    <>
      <div className="ipCardDiv">
        <div style={{ position: "relative", height: "450px", width: "100%" }}>
          {loadingData && (
            <Spin
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1000,
              }}
            />
          )}
          <MapContainer
            minZoom={1}
            center={[20, 0]}
            zoom={2}
            style={{ height: "100%", width: "100%" }}
            maxBounds={[
              [-90, -180],
              [90, 180],
            ]}
            maxBoundsViscosity={1.0}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

            <MarkerClusterGroup chunkedLoading>
              {adjustedData.map((item) => (
                <Marker
                  key={item.id}
                  position={[item.latitude, item.longitude]}
                  icon={createIcon(item.status)}
                  eventHandlers={{
                    click: () => handleMarkerClick(item.id),
                    mouseover: (e) => e.target.openTooltip(),
                    mouseout: (e) => e.target.closeTooltip(),
                  }}
                >
                  <Tooltip direction="top" offset={[0, -40]} opacity={1}>
                    {item.name}
                  </Tooltip>
                </Marker>
              ))}
            </MarkerClusterGroup>
          </MapContainer>
        </div>
      </div>

      <Preview
        visible={showPreviewModal}
        onClose={handleClosePreviewModal}
        siteVisitCardId={selectedSiteVisit}
      />
    </>
  );
};

export default Map;
